import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  KeyRound,
  PowerOff,
  RefreshCw,
  ShieldOff,
  Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { LiveCountdown } from '@/components/ui/LiveCountdown';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useCustomers } from '@/hooks/useCustomers';
import {
  useActiveSession,
  useKillHost,
  useRevokeSession,
  useSessionActiveHosts,
  useStartSession,
  type ActiveHostView,
} from '@/hooks/useSessions';
import { formatDateTime, formatRelative } from '@/lib/format';
import type { Session } from '@/types/api';

/**
 * Session-Manager (WT-304, Sprint 3).
 *
 * - Kein active Session: Start-Card mit Yubikey-Hinweis, optional
 *   Customer-Multi-Select für eager warm-up.
 * - Active Session: Header mit Live-Countdown, Tabelle der Active-Hosts
 *   (TanStack-Query refetchInterval 5s), kill / revoke.
 *
 * Reload-Robust: Zustand kommt aus `/sessions/current`, nicht aus Browser-
 * Storage. Kein History-Mutation beim Start (URL bleibt /sessions).
 */

export const Route = createFileRoute('/sessions/')({
  component: SessionsPage,
});

function SessionsPage(): JSX.Element {
  const sessionQuery = useActiveSession();

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Wartungs-Session</h1>
        <p className="text-muted">
          Yubikey-Touch entsperrt den ssh-agent für 4h. Master-Sockets bleiben
          während der Session warm – keine weiteren Touches nötig.
        </p>
      </header>

      {sessionQuery.isLoading && <SkeletonRows rows={3} cols={3} />}
      {sessionQuery.isError && <ErrorAlert error={sessionQuery.error} />}

      {sessionQuery.data ? (
        <ActiveSessionCard session={sessionQuery.data} />
      ) : sessionQuery.isLoading ? null : (
        <StartSessionCard />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Start-Card (keine aktive Session)
// ---------------------------------------------------------------------------

function StartSessionCard(): JSX.Element {
  const customersQuery = useCustomers({ limit: 200 });
  const customers = customersQuery.data?.data ?? [];
  const start = useStartSession();

  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [ttlHours, setTtlHours] = useState(4);

  const toggleCustomer = (id: string): void => {
    setSelectedCustomerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const onStart = (): void => {
    start.mutate({
      ttlHours,
      ...(selectedCustomerIds.length > 0 ? { customerIds: selectedCustomerIds } : {}),
    });
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <KeyRound className="size-5 text-primary" aria-hidden="true" />
          <span>Session starten</span>
        </span>
      }
      description="1× Yubikey-Touch + Passphrase startet die Wartungs-Session. TTL Default 4h."
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
        <div>
          <p className="text-sm text-muted">
            <strong>Optional:</strong> Hosts vorab aufwärmen, z.B. für alle Systeme
            eines Kunden. Pro Host wird ein Yubikey-Touch fällig – das spart
            später den Touch beim Erstkontakt.
          </p>

          <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-border">
            {customersQuery.isLoading && <SkeletonRows rows={4} cols={2} />}
            {customersQuery.isError && <ErrorAlert error={customersQuery.error} />}
            {!customersQuery.isLoading && customers.length === 0 && (
              <p className="p-3 text-sm text-muted">Keine Kunden gefunden.</p>
            )}
            <ul className="divide-y divide-border">
              {customers.map((c) => (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-2">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-border accent-primary"
                      checked={selectedCustomerIds.includes(c.id)}
                      onChange={() => toggleCustomer(c.id)}
                      aria-label={`${c.name} für Pre-Warmup ausweahlen`}
                    />
                    <span className="flex-1 text-sm">{c.name}</span>
                    <span className="text-xs text-muted">{c.slug}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
          {selectedCustomerIds.length > 0 && (
            <p className="mt-2 text-xs text-muted">
              <strong>{selectedCustomerIds.length}</strong> Kunde(n) ausgewählt –
              alle Hosts werden vorab aufgewärmt (1 Touch pro Host).
            </p>
          )}
        </div>

        <aside className="flex flex-col gap-3 rounded-md border border-border p-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">TTL (Stunden)</span>
            <input
              type="number"
              min={1}
              max={24}
              value={ttlHours}
              onChange={(e) => setTtlHours(Math.max(1, Math.min(24, Number(e.target.value) || 4)))}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <span className="text-xs text-muted">
              Master-Sockets bleiben so lange warm. ControlPersist im Backend.
            </span>
          </label>

          <Button
            leftIcon={<KeyRound className="size-4" />}
            onClick={onStart}
            loading={start.isPending}
            className="w-full"
          >
            Session starten
          </Button>
          {start.isError && <ErrorAlert error={start.error} />}
          {start.isPending && (
            <p className="text-xs text-muted">
              Yubikey berühren ... Pre-Warmup läuft, das kann je nach Host-Anzahl
              ein paar Sekunden dauern.
            </p>
          )}
        </aside>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Active-Card (laufende Session)
// ---------------------------------------------------------------------------

function ActiveSessionCard({ session }: { session: Session }): JSX.Element {
  const hostsQuery = useSessionActiveHosts(session.id);
  const revoke = useRevokeSession();
  const killHost = useKillHost();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hosts = hostsQuery.data ?? [];

  const onRevoke = (): void => {
    setConfirmOpen(false);
    revoke.mutate(session.id);
  };

  return (
    <>
      <Card
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="size-5 text-success" aria-hidden="true" />
            <span>Session aktiv</span>
            <Badge tone="success">{session.status}</Badge>
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-3">
            <span>
              gestartet {formatRelative(session.started_at)} (
              {formatDateTime(session.started_at)})
            </span>
            <span aria-hidden="true">·</span>
            <span>
              TTL läuft in{' '}
              <strong>
                <LiveCountdown target={session.expires_at} />
              </strong>{' '}
              ab
            </span>
          </span>
        }
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="size-4" />}
              onClick={() => void hostsQuery.refetch()}
              loading={hostsQuery.isFetching}
            >
              Aktualisieren
            </Button>
            <Button
              variant="danger"
              leftIcon={<ShieldOff className="size-4" />}
              onClick={() => setConfirmOpen(true)}
              loading={revoke.isPending}
            >
              Session beenden
            </Button>
          </>
        }
      >
        {hostsQuery.isLoading && <SkeletonRows rows={3} cols={5} />}
        {hostsQuery.isError && <ErrorAlert error={hostsQuery.error} />}
        {!hostsQuery.isLoading && hosts.length === 0 && (
          <EmptyState
            icon={<KeyRound className="size-8" />}
            title="Noch keine Hosts in dieser Session"
            description="Pre-warmen oder ersten ssh-Befehl absetzen. Sobald ein Master-Socket entsteht, erscheint er hier (Live-Update alle 5s)."
          />
        )}
        {hosts.length > 0 && (
          <ActiveHostsTable
            hosts={hosts}
            onKill={(alias) =>
              killHost.mutate({ sessionId: session.id, hostAlias: alias })
            }
            killing={killHost.isPending}
          />
        )}
        {revoke.isError && <ErrorAlert error={revoke.error} className="mt-3" />}
        {killHost.isError && <ErrorAlert error={killHost.error} className="mt-3" />}
      </Card>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Session wirklich beenden?"
        description="Alle Master-Sockets dieser Session werden geschlossen. Folgende SSH-Operationen erfordern wieder einen Yubikey-Touch pro Host."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              leftIcon={<PowerOff className="size-4" />}
              onClick={onRevoke}
              loading={revoke.isPending}
            >
              Beenden
            </Button>
          </>
        }
      >
        <p className="inline-flex items-start gap-2 text-sm text-muted">
          <AlertTriangle className="mt-0.5 size-4 text-warning" aria-hidden="true" />
          <span>
            {hosts.length > 0
              ? `${hosts.length} aktive Sockets werden geschlossen.`
              : 'Es sind aktuell keine Hosts kontaktiert.'}
          </span>
        </p>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Active-Hosts-Table (WT-304 Carry-Over: Customer/System sichtbar + sortable)
// ---------------------------------------------------------------------------

type ActiveHostsSortKey = 'customer' | 'system';

interface ResolvedHost {
  raw: ActiveHostView;
  customerName: string;
  systemHostname: string;
  fallback: boolean;
}

/**
 * Backend liefert customer_name / system_hostname optional mit. Fehlt das
 * (z.B. weil der Host gerade erst gewärmt wurde und der Resolver noch nicht
 * gerannt ist), parsen wir host_alias als best-effort: das Naming-Schema ist
 * `<customer-slug>__<system-hostname>`.
 *
 * Exportiert für Tests.
 */
export function resolveActiveHost(host: ActiveHostView): ResolvedHost {
  const customer = host.customer_name ?? null;
  const system = host.system_hostname ?? null;
  if (customer && system) {
    return { raw: host, customerName: customer, systemHostname: system, fallback: false };
  }
  // Fallback-Parsing: split bei Doppel-Underscore.
  const idx = host.host_alias.indexOf('__');
  if (idx > 0) {
    const left = host.host_alias.slice(0, idx);
    const right = host.host_alias.slice(idx + 2);
    return {
      raw: host,
      customerName: customer ?? left,
      systemHostname: system ?? right,
      fallback: true,
    };
  }
  return {
    raw: host,
    customerName: customer ?? '–',
    systemHostname: system ?? host.host_alias,
    fallback: true,
  };
}

interface ActiveHostsTableProps {
  hosts: ActiveHostView[];
  onKill: (hostAlias: string) => void;
  killing: boolean;
}

export function ActiveHostsTable({
  hosts,
  onKill,
  killing,
}: ActiveHostsTableProps): JSX.Element {
  const [sortKey, setSortKey] = useState<ActiveHostsSortKey>('customer');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const resolved = useMemo(() => hosts.map(resolveActiveHost), [hosts]);

  const sorted = useMemo(() => {
    const arr = [...resolved];
    arr.sort((a, b) => {
      const av =
        sortKey === 'customer'
          ? `${a.customerName}${a.systemHostname}`
          : `${a.systemHostname}${a.customerName}`;
      const bv =
        sortKey === 'customer'
          ? `${b.customerName}${b.systemHostname}`
          : `${b.systemHostname}${b.customerName}`;
      const cmp = av.localeCompare(bv, 'de-AT', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [resolved, sortKey, sortDir]);

  const columns = useMemo((): ReadonlyArray<Column<ResolvedHost>> => {
    const toggleSort = (key: ActiveHostsSortKey): void => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    };
    return [
      {
        id: 'kunde',
        header: (
          <SortHeader
            label="Kunde"
            active={sortKey === 'customer'}
            dir={sortDir}
            onClick={() => toggleSort('customer')}
          />
        ),
        filterLabel: 'Kunde',
        filterValue: (r) => r.customerName,
        cell: (r) => (
          <>
            <span className="font-medium">{r.customerName}</span>
            {r.fallback && (
              <span
                className="ml-1 text-xs text-muted"
                title="Aus host_alias geparst (Backend hat customer_name nicht beigeliefert)"
              >
                *
              </span>
            )}
          </>
        ),
      },
      {
        id: 'system',
        header: (
          <SortHeader
            label="System"
            active={sortKey === 'system'}
            dir={sortDir}
            onClick={() => toggleSort('system')}
          />
        ),
        filterLabel: 'System',
        filterValue: (r) => r.systemHostname,
        cell: (r) => <span className="font-mono text-xs">{r.systemHostname}</span>,
      },
      {
        id: 'alias',
        header: 'Alias',
        filterValue: (r) => r.raw.host_alias,
        cell: (r) => (
          <span className="font-mono text-xs text-muted">{r.raw.host_alias}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (r) => (hostIsAlive(r.raw) ? 'alive' : 'idle'),
        cell: (r) =>
          hostIsAlive(r.raw) ? (
            <Badge tone="success">alive</Badge>
          ) : (
            <Badge tone="neutral">idle</Badge>
          ),
      },
      {
        id: 'last-used',
        header: 'Last-Used',
        className: 'text-xs',
        filterValue: (r) => {
          const label = formatRelative(r.raw.last_used_at);
          return label === '–' ? '' : label;
        },
        cell: (r) => formatRelative(r.raw.last_used_at),
      },
      {
        id: 'commands',
        header: 'Commands',
        filterValue: (r) => String(r.raw.command_count ?? 0),
        cell: (r) => (
          <span className="font-mono text-xs">{r.raw.command_count ?? 0}</span>
        ),
      },
      {
        id: 'aktion',
        header: 'Aktion',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (r) => (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onKill(r.raw.host_alias)}
            loading={killing}
            leftIcon={<PowerOff className="size-3" />}
          >
            Kicken
          </Button>
        ),
      },
    ];
  }, [sortKey, sortDir, onKill, killing]);

  return (
    <DataTable
      caption="Aktive Master-Sockets"
      columns={columns}
      rows={sorted}
      rowKey={(r) => `${r.raw.host_alias}-${r.raw.first_seen_at}`}
      storageKey="master-sockets"
    />
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
}): JSX.Element {
  // aria-sort gehört eigentlich auf <th>, aber das würde mit der Table-
  // Wrapper-Komponente kollidieren. Wir packen es als data-attr und
  // signalisieren den State via aria-pressed (toggle-button-Semantik).
  const sortLabel = active
    ? `${label} sortiert ${dir === 'asc' ? 'aufsteigend' : 'absteigend'}`
    : `${label} sortieren`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={sortLabel}
      data-sort={active ? dir : 'none'}
      className="inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:text-text"
    >
      {label}
      {active &&
        (dir === 'asc' ? (
          <ArrowUp className="size-3" aria-hidden="true" />
        ) : (
          <ArrowDown className="size-3" aria-hidden="true" />
        ))}
    </button>
  );
}

/** Backend liefert `alive`; sonst last_used_at < 60s = alive. */
function hostIsAlive(host: ActiveHostView): boolean {
  if (typeof host.alive === 'boolean') return host.alive;
  const last = new Date(host.last_used_at).getTime();
  return Date.now() - last <= 60_000;
}
